import { buildApiUrl, buildAuthHeaders, servicePost } from "../../api/client.ts";
import { issaMediaTypeFor, toIssaFileRules, type IssaFileRules, type IssaFileRulesResponse } from "./issaAttachments.ts";

/* ── Types ───────────────────────────────────────────── */

export interface SuggestionResponse {
  Title: string;
  Question: string;
  ModelConfigId?: string;
}

export interface ConversationResponse {
  ConversationGUID: string;
  Title: string;
  Pinned: boolean;
  Started: string; // ISO date from server
  ModelConfigId?: string;
}

export interface AgentPromptResult {
  Id: number;
  Title: string;
  Result: string;
}

export interface LogResponse {
  Id: number;
  ConversationGUID: string;
  Prompt: string;
  Response: string;
  /** When the prompt was submitted. */
  When: string;
  /** When the AI response finished; null on rows from before the column existed. */
  Updated?: string | null;
  /** The caller's rating of this answer: 1 = Bad, 2 = Fine, 3 = Good; null when not rated. */
  FeedbackRating?: number | null;
  /** The files this answer offered; left out when it offered none. */
  Files?: IssaGeneratedFile[] | null;
  /** The answer read a file the user attached; left out when it did not. */
  UsedAttachments?: boolean;
}

/** A file an answer offered: a copy in the user's My files. The answer links to it
 *  with `<file id="{ExportId}"></file>`. */
export interface IssaGeneratedFile {
  /** 32 hex digits, as in the answer's tag. */
  ExportId: string;
  FileName: string;
  MediaType?: string | null;
  SizeBytes?: number;
  Created?: string;
  /** The copy is gone from My files (pruned for space or by age). */
  Expired?: boolean;
}

export interface IssaConversationFile {
  FileGuid: string;
  FileName: string;
  SizeBytes: number;
  UploadedAt: string;
  /** txt, csv, tsv, json or xlsx; missing on files attached before it was recorded. */
  Kind?: string | null;
}

interface IssaFileResponse<T> {
  bError: boolean;
  sMessage?: string;
  oData: T;
}

/* ── API calls ───────────────────────────────────────── */

/** Generate a 32-char hex GUID matching the server format (Guid.ToString("N")). */
export function generateConversationGuid(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

export async function getSuggestions(): Promise<SuggestionResponse[]> {
  const r = await servicePost<SuggestionResponse[]>("IssaInsightsGetSuggestions", {});
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load suggestions");
  return r.oData ?? [];
}

export async function getConversations(): Promise<ConversationResponse[]> {
  const r = await servicePost<ConversationResponse[]>("IssaInsightsGetConversations", {});
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load conversations");
  return r.oData ?? [];
}

export async function getConversationLogs(guid: string): Promise<LogResponse[]> {
  const r = await servicePost<LogResponse[]>("IssaInsightsGetConversationLogs", { guid });
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load conversation logs");
  return r.oData ?? [];
}

export async function getConversationFiles(guid: string): Promise<IssaConversationFile[]> {
  const r = await servicePost<IssaConversationFile[]>("IssaInsightsGetConversationFiles", { guid });
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load conversation files");
  return r.oData ?? [];
}

/** What the attach button may offer in a conversation with `modelConfigId`: pictures only when
 *  its model can see them. Today's values, without pictures, when the server cannot say. */
export async function getFileRules(modelConfigId?: string): Promise<IssaFileRules> {
  try {
    const r = await servicePost<IssaFileRulesResponse>("IssaInsightsGetFileRules", modelConfigId ? { modelConfigId } : {});
    return r.bError ? toIssaFileRules(null) : toIssaFileRules(r.oData);
  } catch {
    return toIssaFileRules(null);
  }
}

export async function prepareConversation(guid: string, prompt: string, modelConfigId: string): Promise<void> {
  const r = await servicePost<boolean>("IssaInsightsPrepareConversation", { guid, prompt, modelConfigId });
  if (r.bError || !r.oData) throw new Error(r.sMessage ?? "Failed to prepare conversation");
}

export async function uploadConversationFile(
  guid: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<IssaConversationFile> {
  const headers = await buildAuthHeaders();
  // The kind by its extension, without a charset: the bytes go as they are, and a
  // Windows-1252 CSV from Excel is read as that. The server goes by the extension.
  const contentType = issaMediaTypeFor(file.name);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildApiUrl(`/api/v2/issa/files?conversationGuid=${encodeURIComponent(guid)}`));
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    xhr.setRequestHeader("X-File-Name", file.name);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      try {
        const response = JSON.parse(xhr.responseText) as IssaFileResponse<IssaConversationFile>;
        if (xhr.status < 200 || xhr.status >= 300 || response.bError) {
          reject(new Error(response.sMessage || `Upload failed: ${xhr.status}`));
          return;
        }
        resolve(response.oData);
      } catch {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(file);
  });
}

export async function deleteConversationFile(guid: string, fileGuid: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/v2/issa/files/${encodeURIComponent(fileGuid)}?conversationGuid=${encodeURIComponent(guid)}`),
    { method: "DELETE", headers: await buildAuthHeaders() },
  );
  const result = await response.json() as IssaFileResponse<boolean>;
  if (!response.ok || result.bError || !result.oData) throw new Error(result.sMessage || "Failed to delete file");
}

export async function submitPrompt(prompt: string, guid: string, modelConfigId?: string, signal?: AbortSignal, displayPrompt?: string): Promise<ConversationResponse> {
  const r = await servicePost<any>("IssaInsightsSubmitPrompt", { prompt, guid, modelConfigId: modelConfigId ?? "default", displayPrompt: displayPrompt ?? null }, signal);
  if (r.bError) throw new Error(r.sMessage ?? "Failed to submit prompt");
  // Handle direct response or async Task-wrapped response from WCF
  const raw = r.oData;
  const data = raw?.ConversationGUID ? raw : raw?.Result;
  if (!data?.ConversationGUID) {
    console.error("[ISSA] Unexpected submitPrompt response shape:", JSON.stringify(raw));
    // An empty envelope (no oData, no bError) means the server could not produce
    // an answer — almost always because the InstantAI runner is unreachable
    // (e.g. running locally without access to the AI backend).
    throw new Error(r.sMessage ?? "The assistant could not reach the AI service. Please try again later.");
  }
  return data as ConversationResponse;
}

export async function deleteConversation(guid: string): Promise<boolean> {
  const r = await servicePost<boolean>("IssaInsightsDeleteConversation", { guid });
  if (r.bError) throw new Error(r.sMessage ?? "Failed to delete conversation");
  return r.oData ?? false;
}

/** Saves the rating of one answer. Leave `comment` out to keep a comment saved earlier. */
export async function saveAnswerFeedback(logId: number, rating: 1 | 2 | 3, comment?: string): Promise<void> {
  const r = await servicePost<boolean>(
    "IssaInsightsSaveAnswerFeedback",
    comment === undefined ? { logId, rating } : { logId, rating, comment },
  );
  if (r.bError || !r.oData) throw new Error(r.sMessage ?? "The feedback was not saved");
}

export async function pinConversation(guid: string, pin: boolean): Promise<boolean> {
  const r = await servicePost<boolean>("IssaInsightsPinConversation", { guid, pin });
  if (r.bError) throw new Error(r.sMessage ?? "Failed to pin conversation");
  return !!r.oData;
}

export async function getOperatorAgentResults(): Promise<AgentPromptResult[]> {
  const r = await servicePost<AgentPromptResult[]>("IssaInsightsGetOperatorAgentResult", {});
  if (r.bError) return [];
  return r.oData ?? [];
}

export async function getPromptStatus(guid: string): Promise<string | null> {
  const r = await servicePost<string>("IssaInsightsGetPromptStatus", { guid });
  if (r.bError) return null;
  return r.oData ?? null;
}

export interface PromptProgress {
  Status?: string | null;
  /** Sanitized answer-so-far streamed by InstantAI; empty string means cleared. */
  PartialAnswer?: string | null;
}

export async function getPromptProgress(guid: string): Promise<PromptProgress | null> {
  const r = await servicePost<PromptProgress>("IssaInsightsGetPromptProgress", { guid });
  if (r.bError) return null;
  return r.oData ?? null;
}
